import { PromoCode } from '@/hooks/sms/types';

/**
 * Replaces promo code placeholders in a message template
 * @param message - The message template containing placeholders
 * @param promoCode - The selected promo code with full details
 * @returns Updated message with placeholders replaced
 */
export function replacePromoPlaceholders(
  message: string,
  promoCode: PromoCode
): string {
  let updatedMessage = message;

  // Replace all instances of [CODE] with actual promo code
  updatedMessage = updatedMessage.replace(/\[CODE\]/g, promoCode.code);

  // Replace [DISCOUNT] with formatted discount value
  if (updatedMessage.includes('[DISCOUNT]')) {
    let discountText = '';

    switch (promoCode.discount_type) {
      case 'percentage_off':
        discountText = `${promoCode.discount_value}% off`;
        break;
      case 'dollar_off':
        discountText = `$${promoCode.discount_value} off`;
        break;
      case 'free_item':
        // For free items, use the full promo name (e.g., "FREE Dessert", "2-for-1 Burgers")
        discountText = `${promoCode.name} on`|| 'FREE';
        break;
      default:
        discountText = '[DISCOUNT]'; // Keep placeholder if unknown type
    }

    updatedMessage = updatedMessage.replace(/\[DISCOUNT\]/g, discountText);
  }

  // Replace [ITEM] - primarily for free_item promos
  if (updatedMessage.includes('[ITEM]')) {
    if (promoCode.discount_type === 'free_item' && promoCode.name) {
      // Use the full promo code name as-is (e.g., "FREE Dessert", "2-for-1 Burgers")
      updatedMessage = updatedMessage.replace(/\[ITEM\]/g, promoCode.name);
    } else {
      // For non-free-item promos, remove the [ITEM] placeholder
      // or keep it for manual replacement
      updatedMessage = updatedMessage.replace(/\s*\[ITEM\]\s*/g, ' ').trim();
    }
  }

  return updatedMessage;
}

/**
 * Checks if a message contains unreplaced promo placeholders
 * @param message - The message to check
 * @returns Array of unreplaced placeholders
 */
export function getUnreplacedPromoPlaceholders(message: string): string[] {
  const placeholders = ['[CODE]', '[DISCOUNT]', '[ITEM]'];
  return placeholders.filter(p => message.includes(p));
}